import { useMemo } from 'react';
import { useContactInformationFields } from 'hooks/useContactInformationFields';
import type { IContactSectionsConfig } from './useSectionsConfig';

interface WizardField {
  name: string;
  id: string;
  isRequired: boolean;
  isSkippable?: boolean;
  skippableTill?: number;
}

export const useContactWizardFields = (sectionsConfig: IContactSectionsConfig): WizardField[] => {
  const { labels } = useContactInformationFields();

  return useMemo(() => {
    const fields: WizardField[] = [];

    // Helper to add field if visible and mandatory
    const addField = (
      config: { isVisible: boolean; isMandatory: boolean; isSkippable?: boolean; skippableTill?: number },
      name: string,
      id: string,
    ) => {
      if (config.isVisible && config.isMandatory) {
        fields.push({ name, id, isRequired: true, isSkippable: config.isSkippable, skippableTill: config.skippableTill });
      } else if (config.isVisible && config.isSkippable) {
        // Also add skippable fields
        fields.push({ name, id, isRequired: false, isSkippable: true, skippableTill: config.skippableTill });
      }
    };

    // Contact Info Section
    if (sectionsConfig.contactInfo.isVisible) {
      addField(sectionsConfig.contactInfo.email, labels.email, 'email');
      addField(sectionsConfig.contactInfo.phone, labels.mobileNumber, 'mobilePhoneNumber');

      // Either email or phone required
      if (
        sectionsConfig.contactInfo.isEitherRequired &&
        !sectionsConfig.contactInfo.email.isMandatory &&
        !sectionsConfig.contactInfo.phone.isMandatory
      ) {
        fields.push({ name: `${labels.email} / ${labels.mobileNumber}`, id: 'emailOrPhone', isRequired: true });
      }

      addField(sectionsConfig.contactInfo.alternatePhone, labels.alternatePhone, 'alternatePhone');
    }

    // Home Address Section
    if (sectionsConfig.homeAddress.isVisible) {
      addField(sectionsConfig.homeAddress.addressLine1, labels.addressLine1, 'homeAddressLine1');
      addField(sectionsConfig.homeAddress.addressLine2, labels.addressLine2, 'homeAddressLine2');
      addField(sectionsConfig.homeAddress.country, labels.country, 'homeAddressCountry');
      addField(sectionsConfig.homeAddress.state, labels.state, 'homeAddressStateProvince');
      addField(sectionsConfig.homeAddress.city, labels.city, 'homeAddressCity');
      addField(sectionsConfig.homeAddress.zipCode, labels.zipCode, 'homeAddressZipPostal');
    }

    // Emergency Contact Section
    if (sectionsConfig.emergencyContact.isVisible) {
      addField(
        sectionsConfig.emergencyContact.emergencyContactName,
        labels.emergencyContactName,
        'emergencyContactName',
      );
      addField(
        sectionsConfig.emergencyContact.emergencyContactRelationship,
        labels.emergencyContactRelationship,
        'emergencyContactRelationship',
      );
      addField(
        sectionsConfig.emergencyContact.emergencyContactNumber,
        labels.emergencyContactMobileNumber,
        'emergencyContactMobilePhone',
      );
    }

    return fields;
  }, [sectionsConfig, labels]);
};
